import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Form,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Nav,
  NavItem,
  NavLink,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { requestJson } from "../../../../helpers/api";
import ConciliarPlataformaPanel, {
  ConciliarPlataformaResponse,
} from "./ConciliarPlataformaPanel";
import MonchisFiltersDrawer from "./MonchisFiltersDrawer";
import MonchisSucursalMappingModal from "./MonchisSucursalMappingModal";
import MonchisAutoConciliacion from "./MonchisAutoConciliacion";

type MovimientoMonchis = {
  id: string;
  nroTransaccion?: string | null;
  nroOrden?: string | null;
  sucursal?: string | null;
  franquicia?: string | null;
  estado?: string | null;
  tipoPagoReal?: string | null;
  montoDelivery?: string | null;
  importeTotalOrden?: string | null;
  importeTotalConDelivery?: string | null;
  porcentajeComision?: string | null;
  fechaHora?: string | null;
  periodo?: string | null;
  creadoEn?: string | null;
  estadoConciliacion?: string | null;
  conciliacion?: {
    estado?: string | null;
    usuario?: string | null;
    fecha?: string | null;
  } | null;
  sucursalMapeo?: {
    idsucursal?: number | null;
    sucursalNombre?: string | null;
    fuente?: string | null;
    empresaNombre?: string | null;
    empresaCodigo?: string | null;
  } | null;
  importacion?: {
    id: number;
    creadoEn: string;
    nombreArchivoOriginal?: string | null;
    usuarioId?: number;
    usuarioNombre?: string | null;
    usuarioCorreo?: string | null;
  };
};

type MovimientosResponse = {
  ok: boolean;
  registros: MovimientoMonchis[];
  total: number;
  page: number;
  limit: number;
};

type MonchisFiltrosOpcionesResponse = {
  ok: boolean;
  sucursales: string[];
  sucursalesMapeadas: string[];
  franquicias: string[];
  tiposPago: string[];
};

type Props = {
  panelProps: React.ComponentProps<typeof ConciliarPlataformaPanel>;
};

type MonchisMovimientosTableProps = {
  movimientos: MovimientoMonchis[];
  cargando: boolean;
  total: number;
  page: number;
  paginasTotales: number;
  onPageChange: (page: number) => void;
};

const filtrosMonchisBase = {
  fechaDesde: "",
  fechaHasta: "",
  estado: "",
  sucursal: "",
  sucursalMapeada: "",
  franquicia: "",
  tipoPago: "",
  importacionId: "",
};

const MonchisMovimientosTable = ({
  movimientos,
  cargando,
  total,
  page,
  paginasTotales,
  onPageChange,
}: MonchisMovimientosTableProps) => (
  <>
    <div className="table-responsive">
      <Table className="align-middle table-nowrap mb-0">
        <thead className="table-light">
          <tr>
            <th>
              <span>Transacción</span>
              <small className="text-muted d-block">Monchis</small>
            </th>
            <th>
              <span>Orden</span>
              <small className="text-muted d-block">Monchis</small>
            </th>
            <th>
              <span>Fecha</span>
              <small className="text-muted d-block">Monchis</small>
            </th>
            <th>
              <span>Sucursal</span>
              <small className="text-muted d-block">Monchis</small>
            </th>
            <th>
              <span>Sucursal mapeada</span>
              <small className="text-muted d-block">JediSoft</small>
            </th>
            <th>
              <span>Estado</span>
              <small className="text-muted d-block">Monchis</small>
            </th>
            <th>
              <span>Importe total</span>
              <small className="text-muted d-block">Monchis</small>
            </th>
            <th>
              <span>Periodo</span>
              <small className="text-muted d-block">Monchis</small>
            </th>
            <th>
              <span>Importación</span>
              <small className="text-muted d-block">BaconApp</small>
            </th>
            <th>
              <span>Estado de conciliación</span>
              <small className="text-muted d-block">BaconApp</small>
            </th>
            <th>
              <span>Auditoría</span>
              <small className="text-muted d-block">BaconApp</small>
            </th>
          </tr>
        </thead>
        <tbody>
          {cargando ? (
            <tr>
              <td colSpan={11} className="text-center py-4">
                <Spinner size="sm" className="me-2" /> Cargando movimientos…
              </td>
            </tr>
          ) : movimientos.length === 0 ? (
            <tr>
              <td colSpan={11} className="text-center py-4 text-muted">
                No hay movimientos registrados con los filtros actuales.
              </td>
            </tr>
          ) : (
            movimientos.map((movimiento) => (
              <tr key={movimiento.id}>
                <td>{movimiento.nroTransaccion || "—"}</td>
                <td>{movimiento.nroOrden || "—"}</td>
                <td>
                  {movimiento.fechaHora
                    ? new Date(movimiento.fechaHora).toLocaleString("es-PY")
                    : "Sin fecha"}
                </td>
                <td>
                  <div className="fw-semibold">{movimiento.sucursal || "Sin sucursal"}</div>
                  <div className="text-muted small">{movimiento.franquicia || "—"}</div>
                </td>
                <td>
                  {movimiento.sucursalMapeo ? (
                    <>
                      <div className="fw-semibold">
                        {movimiento.sucursalMapeo.sucursalNombre || "Sin nombre"}
                      </div>
                      <div className="text-muted small">
                        #{movimiento.sucursalMapeo.idsucursal ?? "Sin ID"} ·{" "}
                        {movimiento.sucursalMapeo.fuente || "Sin fuente"}
                      </div>
                    </>
                  ) : (
                    <span className="text-muted">Pendiente de mapeo</span>
                  )}
                </td>
                <td>
                  <Badge color={movimiento.estado === "Entregada" ? "success" : "secondary"}>
                    {movimiento.estado || "Sin estado"}
                  </Badge>
                </td>
                <td className="text-end">
                  {(Number(movimiento.importeTotalConDelivery ?? movimiento.importeTotalOrden ?? 0) || 0).toLocaleString(
                    "es-PY",
                    { minimumFractionDigits: 0, maximumFractionDigits: 0 },
                  )}{" "}
                  Gs.
                </td>
                <td>{movimiento.periodo || "—"}</td>
                <td>
                  {movimiento.importacion ? (
                    <>
                      <div className="small text-muted">#{movimiento.importacion.id}</div>
                      <div className="small">
                        {new Date(movimiento.importacion.creadoEn).toLocaleDateString("es-PY")}
                      </div>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {(() => {
                    const estado =
                      movimiento.conciliacion?.estado ??
                      movimiento.estadoConciliacion ??
                      "Sin conciliación";
                    const texto = estado || "Sin conciliación";
                    const estadoNormalizado = texto.toLowerCase();
                    const esConciliado =
                      estadoNormalizado === "conciliado" ||
                      estadoNormalizado === "conciliado parcial";
                    return (
                      <>
                        <Badge color={esConciliado ? "success" : "secondary"}>
                          {texto}
                        </Badge>
                        <div className="text-muted small mt-1">
                          {movimiento.conciliacion?.usuario
                            ? `${movimiento.conciliacion.usuario} · ${
                                movimiento.conciliacion.fecha
                                  ? new Date(movimiento.conciliacion.fecha).toLocaleString("es-PY")
                                  : "Sin fecha"
                              }`
                            : "Sin detalle"}
                        </div>
                      </>
                    );
                  })()}
                </td>
                <td>
                  <div className="fw-semibold">
                    {movimiento.importacion?.usuarioNombre ||
                      (movimiento.importacion?.usuarioId
                        ? `Usuario #${movimiento.importacion.usuarioId}`
                        : "Sin usuario")}
                  </div>
                  <div className="text-muted small">
                    {movimiento.creadoEn
                      ? new Date(movimiento.creadoEn).toLocaleString("es-PY")
                      : movimiento.importacion?.creadoEn
                        ? new Date(movimiento.importacion.creadoEn).toLocaleString("es-PY")
                        : "Sin fecha"}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
    {paginasTotales > 1 && (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Página {page + 1} de {paginasTotales} · Total {total} registros
        </div>
        <div className="d-flex gap-2">
          <Button color="light" disabled={page === 0} onClick={() => onPageChange(Math.max(page - 1, 0))}>
            Anterior
          </Button>
          <Button
            color="light"
            disabled={page + 1 >= paginasTotales}
            onClick={() => onPageChange(Math.min(page + 1, paginasTotales - 1))}
          >
            Siguiente
          </Button>
        </div>
      </div>
    )}
  </>
);

const MonchisConciliacionPanel = ({ panelProps }: Props) => {
  const [movimientos, setMovimientos] = useState<MovimientoMonchis[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(25);
  const [buscar, setBuscar] = useState("");
  const [periodo, setPeriodo] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [recargaVersion, setRecargaVersion] = useState(0);
  const [filtrosVisibles, setFiltrosVisibles] = useState(false);
  const [filtros, setFiltros] = useState(() => ({ ...filtrosMonchisBase }));
  const [filtrosAplicados, setFiltrosAplicados] = useState(() => ({ ...filtrosMonchisBase }));
  const [importSummary, setImportSummary] = useState<ConciliarPlataformaResponse | null>(null);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [opcionesFiltros, setOpcionesFiltros] = useState({
    sucursales: [] as string[],
    sucursalesMapeadas: [] as string[],
    franquicias: [] as string[],
    tiposPago: [] as string[],
  });
  const [cargandoOpciones, setCargandoOpciones] = useState(false);
  const [errorOpciones, setErrorOpciones] = useState<string | null>(null);
  const cargarMovimientos = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      params.set("page", String(page));
      if (buscar.trim()) {
        params.set("buscar", buscar.trim());
      }
      if (periodo.trim()) {
        params.set("periodo", periodo.trim());
      }
      if (filtrosAplicados.fechaDesde) {
        params.set("fechaDesde", filtrosAplicados.fechaDesde);
      }
      if (filtrosAplicados.fechaHasta) {
        params.set("fechaHasta", filtrosAplicados.fechaHasta);
      }
      if (filtrosAplicados.estado) {
        params.set("estado", filtrosAplicados.estado);
      }
      if (filtrosAplicados.sucursal.trim()) {
        params.set("sucursal", filtrosAplicados.sucursal.trim());
      }
      if (filtrosAplicados.sucursalMapeada.trim()) {
        params.set("sucursalMapeada", filtrosAplicados.sucursalMapeada.trim());
      }
      if (filtrosAplicados.franquicia.trim()) {
        params.set("franquicia", filtrosAplicados.franquicia.trim());
      }
      if (filtrosAplicados.tipoPago.trim()) {
        params.set("tipoPago", filtrosAplicados.tipoPago.trim());
      }
      if (filtrosAplicados.importacionId.trim()) {
        params.set("importacionId", filtrosAplicados.importacionId.trim());
      }
      const respuesta = await requestJson<MovimientosResponse>(
        `/api/tesoreria/importaciones/monchis/movimientos?${params.toString()}`,
      );
      setMovimientos(respuesta.registros ?? []);
      setTotal(respuesta.total ?? 0);
    } catch (err) {
      const mensaje =
        err instanceof Error ? err.message : "No se pudieron cargar los movimientos.";
      setError(mensaje);
    } finally {
      setCargando(false);
    }
  }, [buscar, periodo, limit, page, filtrosAplicados, recargaVersion]);

  useEffect(() => {
    void cargarMovimientos();
  }, [cargarMovimientos]);

  useEffect(() => {
    let cancelado = false;
    const cargarOpciones = async () => {
      setCargandoOpciones(true);
      setErrorOpciones(null);
      try {
        const respuesta = await requestJson<MonchisFiltrosOpcionesResponse>(
          "/api/tesoreria/importaciones/monchis/filtros",
        );
        if (cancelado) {
          return;
        }
        setOpcionesFiltros({
          sucursales: respuesta.sucursales ?? [],
          sucursalesMapeadas: respuesta.sucursalesMapeadas ?? [],
          franquicias: respuesta.franquicias ?? [],
          tiposPago: respuesta.tiposPago ?? [],
        });
      } catch (err) {
        if (cancelado) {
          return;
        }
        const mensaje =
          err instanceof Error
            ? err.message
            : "No se pudieron cargar las opciones de filtros de Monchis.";
        setErrorOpciones(mensaje);
      } finally {
        if (!cancelado) {
          setCargandoOpciones(false);
        }
      }
    };
    void cargarOpciones();
    return () => {
      cancelado = true;
    };
  }, []);

  const paginasTotales = useMemo(() => Math.ceil(total / limit), [total, limit]);

  const handleBuscar = () => {
    setFiltrosAplicados(filtros);
    setPage(0);
    setRecargaVersion((prev) => prev + 1);
  };

  const handleRecargar = () => {
    setRecargaVersion((prev) => prev + 1);
  };

  const handleFiltrosChange = (campo: keyof typeof filtros, valor: string) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  };

  const limpiarFiltros = (aplicar = true) => {
    const base = { ...filtrosMonchisBase };
    setFiltros(base);
    if (aplicar) {
      setFiltrosAplicados(base);
      setPeriodo("");
      setBuscar("");
      setPage(0);
      setRecargaVersion((prev) => prev + 1);
    }
  };

  const toggleFiltros = () => setFiltrosVisibles((prev) => !prev);

  const aplicarFiltrosDesdeDrawer = () => {
    handleBuscar();
    setFiltrosVisibles(false);
  };

  const limpiarFiltrosDesdeDrawer = () => {
    limpiarFiltros(false);
    setFiltrosVisibles(false);
  };

  const handleSubmitBusqueda = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleBuscar();
  };

  const limpiarBusquedaRapida = () => {
    if (!buscar) return;
    setBuscar("");
    handleBuscar();
  };

  const limpiarPeriodoRapido = () => {
    if (!periodo) return;
    setPeriodo("");
    handleBuscar();
  };

  const toggleModal = () =>
    setModalVisible((prev) => {
      const siguiente = !prev;
      if (!siguiente) {
        setImportSummary(null);
      }
      return siguiente;
    });

  const handleSuccessImport = (resultado: ConciliarPlataformaResponse) => {
    setImportSummary(resultado);
    handleRecargar();
  };

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
            <div>
              <CardTitle tag="h5" className="mb-1">
                Movimientos importados de Monchis
              </CardTitle>
              <p className="text-muted mb-0">
                Revisa los registros importados y su estado actual en Tesorería.
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button color="light" onClick={handleRecargar} disabled={cargando}>
                <i className="ri-refresh-line me-1" />
                Actualizar
              </Button>
              <Button color="primary" outline onClick={() => setFiltrosVisibles(true)}>
                <i className="ri-filter-3-line me-1" />
                Filtros avanzados
              </Button>
              <Button color="info" outline onClick={() => setConfigModalVisible(true)}>
                <i className="ri-settings-3-line me-1" />
                Mapeo de sucursales
              </Button>
              <MonchisAutoConciliacion />
              <Button color="primary" onClick={toggleModal}>
                <i className="ri-upload-2-line me-1" />
                Importar planilla
              </Button>
            </div>
          </div>
          <Form onSubmit={handleSubmitBusqueda} className="mb-3">
            <Row className="g-3">
              <Col md={5}>
                <InputGroup>
                  <InputGroupText>
                    <i className="ri-search-line" />
                  </InputGroupText>
                  <Input
                    placeholder="Buscar por transacción, orden, sucursal…"
                    value={buscar}
                    onChange={(event) => setBuscar(event.target.value)}
                    disabled={cargando}
                  />
                  {buscar && (
                    <Button
                      color="light"
                      type="button"
                      onClick={limpiarBusquedaRapida}
                      disabled={cargando}
                      aria-label="Limpiar búsqueda"
                    >
                      <i className="ri-close-line" />
                    </Button>
                  )}
                  <Button color="secondary" type="submit" disabled={cargando} aria-label="Aplicar búsqueda">
                    <i className="ri-arrow-right-line" />
                  </Button>
                </InputGroup>
              </Col>
              <Col md={3}>
                <InputGroup>
                  <InputGroupText>
                    <i className="ri-calendar-line" />
                  </InputGroupText>
                  <Input
                    placeholder="Periodo (opcional)"
                    value={periodo}
                    onChange={(event) => setPeriodo(event.target.value)}
                    disabled={cargando}
                  />
                  {periodo && (
                    <Button
                      color="light"
                      type="button"
                      onClick={limpiarPeriodoRapido}
                      disabled={cargando}
                      aria-label="Limpiar periodo"
                    >
                      <i className="ri-close-line" />
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>
          </Form>
          <MonchisFiltersDrawer
            isOpen={filtrosVisibles}
            onToggle={toggleFiltros}
            filtros={filtros}
            onChange={handleFiltrosChange}
            onApply={aplicarFiltrosDesdeDrawer}
            onClear={limpiarFiltrosDesdeDrawer}
            cargando={cargando}
            opcionesSucursales={opcionesFiltros.sucursales}
            opcionesSucursalesMapeadas={opcionesFiltros.sucursalesMapeadas}
            opcionesFranquicias={opcionesFiltros.franquicias}
            opcionesTiposPago={opcionesFiltros.tiposPago}
            cargandoOpciones={cargandoOpciones}
            errorOpciones={errorOpciones}
          />
          {error && (
            <Alert color="danger" toggle={() => setError(null)}>
              {error}
            </Alert>
          )}
          <MonchisMovimientosTable
            movimientos={movimientos}
            cargando={cargando}
            total={total}
            page={page}
            paginasTotales={paginasTotales}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      <Modal isOpen={modalVisible} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>Importar planilla de Monchis</ModalHeader>
        <ModalBody>
          <ConciliarPlataformaPanel {...panelProps} layout="plain" onSuccess={handleSuccessImport} />
          {importSummary && (
            <Alert
              color="success"
              className="mt-3"
              toggle={() => setImportSummary(null)}
            >
              <div className="fw-semibold mb-2">
                {importSummary.message ?? "La importación se completó correctamente."}
              </div>
              <ul className="mb-0">
                {importSummary.resumen?.map((linea) => (
                  <li key={linea}>{linea}</li>
                ))}
                {importSummary.detalles
                  ?.filter((linea) => !importSummary.resumen?.includes(linea))
                  .map((linea) => (
                    <li key={linea}>{linea}</li>
                  ))}
              </ul>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggleModal}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
      <MonchisSucursalMappingModal
        isOpen={configModalVisible}
        onClose={() => setConfigModalVisible(false)}
      />
    </>
  );
};

export default MonchisConciliacionPanel;
